import _styled from "@emotion/styled/base";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { IconExpandMore } from '@travel-frontend/icons';
import { useMemo, useRef, useEffect, forwardRef, useCallback, useState } from 'react';
import { CountrySelector, defaultCountries, usePhoneInput } from 'react-international-phone';
import { components } from 'react-select';
import { Message } from '../../primitives/internal/Message/index.js';
import { Select } from '../../primitives/Select/index.js';
import { CountrySelectorButtonWrapper } from '../../primitives/TextInput/components/CountrySelectorButtonWrapper.js';
import { CustomControl } from '../../primitives/TextInput/components/CustomControl.js';
import { Input } from '../../primitives/TextInput/components/Input.js';
import { Label } from '../../primitives/TextInput/components/Label.js';
import { PhoneInputContainer } from '../../primitives/TextInput/components/PhoneInputContainer.js';
import { PhonePrefixContainer } from '../../primitives/TextInput/components/PrefixContainer.js';
import { Wrapper } from '../../primitives/TextInput/components/Wrapper.js';
import 'react-international-phone/style.css';
export const PhoneField = /*#__PURE__*/_styled(forwardRef(function Field({
  id,
  label,
  message,
  name,
  value,
  disabled,
  invalid,
  fullWidth,
  visuallyFocused,
  className,
  onFocus,
  onBlur,
  size = 'medium',
  variant = 'info',
  allowOverflow,
  type = 'text',
  inputRef: inputRefFromProps = null,
  defaultCountry = 'de',
  onSelectedCountryCodeChange,
  selectedCountry,
  ...restProps
}, ref) {
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const {
    inputValue: phonePrefix,
    inputRef: inputRefPhone,
    country,
    setCountry
  } = usePhoneInput({
    defaultCountry: defaultCountry,
    value: ''
  });
  useEffect(() => {
    if (phonePrefix) onSelectedCountryCodeChange?.(phonePrefix);
  }, [phonePrefix]);
  useEffect(() => {
    if (selectedCountry && selectedCountry !== country.iso2) {
      setCountry(selectedCountry);
    }
  }, [selectedCountry]);
  const inputRef = inputRefFromProps || inputRefPhone;
  const priorityOrder = ['Deutschland', 'Schweiz', 'Österreich'];
  const regionNamesInGerman = new Intl.DisplayNames(['de'], {
    type: 'region'
  });
  const toggleDropdown = () => {
    setTimeout(() => {
      selectRef.current?.focus();
    }, 0);
    setIsOpen(previous => !previous);
  };
  const handleChange = newValue => {
    const option = newValue;
    if (option) {
      setCountry(option.value);
      setIsOpen(false);
    }
  };
  const convertedCountryData = useMemo(() => {
    return defaultCountries.map(([countryName, iso2, ...rest]) => {
      const countryNameInGerman = regionNamesInGerman.of(iso2.toUpperCase()) || countryName;
      return [countryNameInGerman, iso2, ...rest];
    }).toSorted((a, b) => {
      const aIndex = priorityOrder.indexOf(a[0]);
      const bIndex = priorityOrder.indexOf(b[0]);
      const aIsPriority = aIndex !== -1;
      const bIsPriority = bIndex !== -1;
      if (aIsPriority && bIsPriority) {
        return aIndex - bIndex;
      }
      if (aIsPriority) return -1;
      if (bIsPriority) return 1;
      return a[0].localeCompare(b[0]);
    }).map(entry => {
      const [label, iso2, dialCode] = entry;
      return {
        value: iso2,
        label,
        dialCode
      };
    });
  }, [defaultCountries]);
  const handleFocus = useCallback(event => {
    if (onFocus) {
      onFocus(event);
    }
    setFocused(true);
  }, [onFocus]);
  const handleBlur = useCallback(event => {
    if (onBlur) {
      onBlur(event);
    }
    setFocused(false);
  }, [onBlur]);
  const hiddenElementStyle = {
    display: 'none'
  };
  const customControl = props => _jsx(CustomControl, {
    children: _jsx(components.Control, {
      ...props
    })
  });
  return _jsxs(Wrapper, {
    fullWidth: fullWidth,
    ref: ref,
    children: [_jsxs(PhoneInputContainer, {
      className: className,
      invalid: invalid,
      disabled: disabled,
      // @ts-expect-error TS(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
      focused: focused || visuallyFocused,
      variant: variant,
      children: [_jsx(CountrySelector, {
        selectedCountry: country.iso2,
        dropdownArrowStyle: hiddenElementStyle,
        renderButtonWrapper: ({
          children,
          rootProps
        }) => {
          return _jsxs(CountrySelectorButtonWrapper, {
            tabIndex: 0,
            onClick: e => {
              e.preventDefault();
              toggleDropdown();
            },
            rotate: !!rootProps['aria-expanded'],
            children: [children, _jsx(IconExpandMore, {
              color: "interactionSecondary"
            })]
          });
        },
        onSelect: parsedCountry => {
          setCountry(parsedCountry.iso2);
        }
      }), _jsx(PhonePrefixContainer, {
        children: phonePrefix
      }), _jsx(Input
      // @ts-expect-error (TS2322) FIXME: Type RefObject<HTMLInputElement | null> is not assignable to type Ref<HTMLInputElement> | undefined>
      , {
        // @ts-expect-error (TS2322) FIXME: Type RefObject<HTMLInputElement | null> is not assignable to type Ref<HTMLInputElement> | undefined>
        ref: inputRef,
        id: id,
        allowOverflow: allowOverflow,
        disabled: disabled,
        name: name,
        value: value,
        size: size,
        onFocus: handleFocus,
        onBlur: handleBlur,
        type: type,
        "aria-label": label,
        ...restProps
      }), _jsx(Label, {
        tabIndex: -1,
        "data-testid": "label",
        disabled: disabled,
        htmlFor: id,
        children: label
      }), _jsx(Select, {
        menuIsOpen: isOpen,
        size: "large",
        ref: selectRef,
        onMenuClose: () => setIsOpen(false),
        menuShouldBlockScroll: true,
        onChange: handleChange,
        defaultValue: convertedCountryData[0],
        options: convertedCountryData,
        components: {
          Control: customControl
        }
      })]
    }), message && _jsx(Message, {
      "data-testid": "message",
      invalid: invalid,
      children: message
    })]
  });
}), process.env.NODE_ENV === "production" ? {
  target: "e1gjsbt70"
} : {
  target: "e1gjsbt70",
  label: "PhoneField"
})(process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");